import { from, of } from 'rxjs';
import { catchError, filter, map, mergeMap, scan } from 'rxjs/operators';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { captureException } from '@famly/vet_services_sentry';
import { getApiUrl } from '@famly/stat_ts-utils_api-bridge';
import { fromReadableStream } from './from-readable-stream';
/**
 * A function that calls OpenAI's API through Famly's BE and streams back the model's response.
 *
 * @param endpoint Which famlyapi endpoint to send the request to
 * @param requestOptions Options object to customise the fetch request
 * @param requestId Optional request identifier
 * @param abortSignal Optional AbortSignal to cancel the request
 * @returns An observable that emits `string` and completes with `{stop: true}`. In practice this will
 * emit many times with small strings that are usually only parts of words.
 */
export function streamResponseObservable(endpoint, requestOptions, requestId, abortSignal) {
    const decoder = new TextDecoder();
    return from(fetch(`${getApiUrl()}${endpoint}`, {
        ...requestOptions,
        signal: abortSignal,
    })).pipe(mergeMap(response => {
        return from((async () => {
            if (!response.ok) {
                console.error('HTTP error! status:', response);
                let errorMessage = `HTTP error! status: ${response.status}`;
                // Try to extract error message from response body
                try {
                    const errorBody = await response.text();
                    if (errorBody) {
                        try {
                            const parsedError = JSON.parse(errorBody);
                            errorMessage = parsedError.message || parsedError.error || errorMessage;
                        }
                        catch (_a) {
                            // If JSON parsing fails, use the raw text
                            errorMessage = errorBody;
                        }
                    }
                }
                catch (_b) {
                    // If reading response body fails, use the default message
                }
                const error = new Error(errorMessage);
                error.status = response.status;
                throw error;
            }
            if (!response.body) {
                throw new Error('Response body is null');
            }
            return response.body;
        })()).pipe(mergeMap(body => fromReadableStream(body, undefined, undefined, true)));
    }), map(chunk => decoder.decode(chunk, { stream: true })), scan((acc, chunk) => {
        acc.buffer += chunk;
        const messages = acc.buffer.split('\n\n');
        acc.buffer = messages.pop() || '';
        return { buffer: acc.buffer, messages: messages.filter(msg => msg.startsWith('data: ')) };
    }, { buffer: '', messages: [] }), mergeMap(({ messages }) => from(messages)), map(message => {
        // Removes the 'data: ' prefix
        const data = message.slice(6);
        if (data === '[DONE]') {
            throw new Error('STREAM_DONE');
        }
        try {
            return JSON.parse(data);
        }
        catch (error) {
            console.error('Error parsing JSON:', error);
            captureException(new Error(`Received incomplete data chunk from OpenAI's API`));
            return null;
        }
    }), filter(data => data !== null), map((data) => {
        if (data.choices && data.choices[0]) {
            // eslint-disable-next-line local-rules/camelcase
            const { delta, finish_reason } = data.choices[0];
            if (delta && 'content' in delta && delta.content) {
                return { content: delta.content, requestId };
            }
            else if (hasValue(finish_reason)) {
                return { content: '', requestId };
            }
        }
        return null;
    }), filter(data => data !== null), catchError(error => {
        if (error.message === 'STREAM_DONE') {
            return of({ stop: true, reason: 'DONE' });
        }
        else if (error.name === 'AbortError') {
            return of({ stop: true, reason: 'ABORTED' });
        }
        else if (error.status === 400) {
            console.error('Validation error:', error);
            return of({ stop: true, reason: 'VALIDATION_ERROR', message: error.message });
        }
        else {
            console.error('Stream error:', error);
            return of({ stop: true, reason: 'NETWORK_ERROR' });
        }
    }));
}
